import React, { useState } from 'react';
import { colorPalette, sizes, sizeValues } from '@sharefiledev/antd-config';
import { Close, HelpQuestion, KeyboardShortcut } from '@sharefiledev/icons';
import { useMutation } from '@tanstack/react-query';
import { App, Button, Flex, Grid, Layout, Typography } from 'antd';
import BrandingLogo from '../../Common/Components/BrandingLogo/BrandingLogo';
import { ComponentTypes } from '../../Common/Components/types';
import {
	collectIdsByType,
	filterIdsByComponentType,
} from '../../Common/ComponentsRenderer/util';
import { useAppStore } from '../../Common/store';
import { BrandingConstants, SignerPageTabIndexes } from '../../constants';
import { ESignatureClient } from '../../data/eSignatureClient';
import {
	Account,
	BaseFiles,
	SignerResponseStatus,
	SignerSubmissionRequest,
	SignerSubmissionResponse,
} from '../../data/rsTypes';
import { t } from '../../util';
import { ShortcutsModal } from '../Modals/ShortcutsModal';
import { HelpAndFaqDrawer } from '../PageContent/HelpAndFaqDrawer';
import { SubmitResponseButton } from '../SubmitResponseButton/SubmitResponseButton';
import { DocumentActionsDrawer } from './DocumentActions/DocumentActions';
const { Title } = Typography;
const { useBreakpoint } = Grid;

const { Header } = Layout;

const headerFlexStyles: React.CSSProperties = {
	height: '100%',
};
const titleStyles: React.CSSProperties = {
	marginTop: sizes.XXS,
	marginBottom: sizes.XXS,
	fontSize: sizes.base,
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	textWrap: 'nowrap',
	display: 'flex',
	alignItems: 'center',
};
export interface PageHeaderProps {
	baseFiles: BaseFiles;
	account: Account;
	setSubmitResponse: (submitResponse: SignerSubmissionResponse) => void;
	setIsSignerPageDrawerOpen?: (isOpen: boolean) => void;
	handleDeclineDocument?: (documentId: string, documentName: string) => void;
	isContentOnlyMode?: boolean;
	cancelInteraction?: () => void;
}

export const PageHeader: React.FC<PageHeaderProps> = props => {
	const { notification } = App.useApp();
	const { isContentOnlyMode, cancelInteraction } = props;
	const screens = useBreakpoint();
	const [esignDocument, isSignFromDashboardFlow, signer, componentValues, searchParams] =
		useAppStore(store => [
			store.esignDocument,
			store.isSignFromDashboardFlow,
			store.signer,
			store.componentValues,
			store.searchParams,
		]);
	const [isShortcutsModalOpen, setIsShortcutsModalOpen] = useState<boolean>(false);
	const [isHelpAndFaqDrawerOpen, setIsHelpAndFaqDrawerOpen] = useState<boolean>(false);

	const headerStyles: React.CSSProperties = {
		padding: `0 ${screens.xs ? sizes.base : sizes.MD}`,
		borderBottom: `1px solid ${colorPalette.neutral3}`,
	};

	const drawerHeaderStyles: React.CSSProperties = {
		padding: `${sizes.base} ${sizes.MD}`,
		borderBottom: `1px solid ${colorPalette.neutral3}`,
	};

	React.useEffect(() => {
		const handleKeyDown = event => {
			if (event.shiftKey && event.key === '?') {
				setIsShortcutsModalOpen(prev => !prev);
			}
		};

		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, []);

	const signerSavesProgressMutation = useMutation({
		mutationFn: () => {
			const textAnnotationIds = collectIdsByType(
				esignDocument.components,
				c => c.type === ComponentTypes.TextAnnotationComponent
			);
			const filteredValues = filterIdsByComponentType(textAnnotationIds, componentValues);
			const payload: SignerSubmissionRequest = {
				signer: {
					name: signer.name,
					email: signer.email,
					response: {
						status: SignerResponseStatus.Draft,
						component_values: Array.from(filteredValues.values()),
					},
				},
			};

			return ESignatureClient.submitSignerResponse(signer.id, searchParams, payload);
		},
		onSuccess: () => {
			notification.info({
				message: t('esign-pilet-ui:signerPage.header.notifications.saveProgressSuccess'),
				placement: 'topRight',
			});
		},
		onError: () => {
			notification.error({
				message: t('esign-pilet-ui:signerPage.header.notifications.saveProgressFailed'),
				placement: 'topRight',
			});
		},
	});

	const handleSignerPageDrawerClose = () => {
		signerSavesProgressMutation.mutateAsync();
		if (isContentOnlyMode) {
			cancelInteraction();
			return;
		}
		props.setIsSignerPageDrawerOpen(false);
	};

	return (
		<>
			<Header style={isContentOnlyMode ? drawerHeaderStyles : headerStyles}>
				<Flex
					gap={screens.xs ? 'small' : 'middle'}
					justify="space-between"
					align="center"
					style={headerFlexStyles}
				>
					<Flex gap={24} style={{ overflow: 'hidden', maxHeight: '100%' }}>
						{screens.lg && <BrandingLogo width={BrandingConstants.PageHeaderLogoWidth} />}
						<Title level={5} style={titleStyles}>
							{esignDocument.name}
						</Title>
					</Flex>
					<Flex justify="space-between" align="center" gap={screens.xs ? 0 : 8}>
						{screens.lg && (
							<Button
								id="keyboard-shortcut-button"
								tabIndex={SignerPageTabIndexes.KeyboardShortcut}
								aria-label={t('esign-pilet-ui:signerPage.header.keyboardShortcuts')}
								size="middle"
								icon={<KeyboardShortcut size={sizeValues.MS} />}
								type="text"
								onClick={() => setIsShortcutsModalOpen(true)}
							></Button>
						)}
						<Button
							id="help-section-button"
							tabIndex={SignerPageTabIndexes.HelpSection}
							aria-label={t('esign-pilet-ui:signerPage.header.helpAndFaq')}
							style={{ marginLeft: 0 }}
							size="middle"
							icon={<HelpQuestion size={sizeValues.MS} />}
							onClick={() => setIsHelpAndFaqDrawerOpen(true)}
							data-testid="help-button"
							type="text"
						></Button>
						<DocumentActionsDrawer
							baseFiles={props.baseFiles}
							account={props.account}
							handleDeclineDocument={props.handleDeclineDocument}
						/>
						{screens.sm && (
							<SubmitResponseButton setSubmitResponse={props.setSubmitResponse} />
						)}
						{isSignFromDashboardFlow && (
							<Button
								icon={<Close size={sizeValues.MS} />}
								onClick={handleSignerPageDrawerClose}
								style={{ border: 'none', color: 'black' }}
								data-testid="signed-in-user-close-button"
							></Button>
						)}
					</Flex>
				</Flex>
			</Header>
			<ShortcutsModal
				isModalOpen={isShortcutsModalOpen}
				setIsModalOpen={setIsShortcutsModalOpen}
			/>
			<HelpAndFaqDrawer
				open={isHelpAndFaqDrawerOpen}
				setIsOpen={setIsHelpAndFaqDrawerOpen}
			/>
		</>
	);
};
